import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, ArrowLeft, ExternalLink } from "lucide-react";
import RichTextEditor from "../../components/RichTextEditor";
import { adminGet, adminPost, adminPut } from "../../lib/api";

const blank = () => ({
  slug: "",
  title: "",
  content_html: "",
  seo_description: "",
  published: true,
});

const PageEditor = () => {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [autoSlug, setAutoSlug] = useState(isNew);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        setForm(await adminGet(`/pages/${id}`));
      } catch (e) {
        toast.error("Failed to load page");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  const update = (k) => (e) => {
    const v = e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((s) => ({ ...s, [k]: v }));
  };

  const slugify = (t) =>
    t.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const onTitleChange = (e) => {
    const title = e.target.value;
    setForm((s) => ({
      ...s,
      title,
      slug: autoSlug ? slugify(title) : s.slug,
    }));
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const payload = {
        slug: form.slug || slugify(form.title),
        title: form.title,
        content_html: form.content_html || "",
        seo_description: form.seo_description || "",
        published: !!form.published,
      };
      const saved = isNew
        ? await adminPost("/pages", payload)
        : await adminPut(`/pages/${id}`, payload);
      toast.success(isNew ? "Page created" : "Page updated");
      if (isNew) navigate(`/admin/pages/${saved.id}`, { replace: true });
      else setForm(saved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-[#0A192F]/40">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading...
      </div>
    );
  }

  return (
    <div data-testid="admin-page-editor" className="space-y-6">
      <Link to="/admin/pages" className="inline-flex items-center gap-2 text-xs font-display uppercase tracking-[0.3em] text-[#0A192F]/60 hover:text-[#D4AF37]">
        <ArrowLeft size={14} /> Back to Pages
      </Link>
      <header className="flex items-end justify-between flex-wrap gap-3">
        <h1 className="font-display font-black text-3xl text-[#0A192F] tracking-tighter">
          {isNew ? "Add New Page" : "Edit Page"}
        </h1>
        <div className="flex items-center gap-3">
          {!isNew && form.published && (
            <a
              href={`/pages/${form.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-gray-300 text-[#0A192F] px-4 py-2 text-xs font-display uppercase tracking-wider font-bold hover:border-[#D4AF37] hover:text-[#D4AF37]"
            >
              <ExternalLink size={13} /> View
            </a>
          )}
          <button
            onClick={save}
            disabled={saving}
            data-testid="page-save-btn"
            className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-5 py-2.5 font-display text-xs uppercase tracking-[0.25em] font-bold hover:bg-[#D4AF37] hover:text-[#0A192F] disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </div>
      </header>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <input
              value={form.title}
              onChange={onTitleChange}
              data-testid="page-title-input"
              placeholder="Enter title here"
              className="w-full border border-gray-300 px-4 py-3.5 text-2xl font-display font-bold text-[#0A192F] focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-[#0A192F]/60 font-display uppercase text-xs tracking-wider">Slug:</span>
            <code className="text-[#0A192F]/70">/pages/</code>
            <input
              value={form.slug}
              onChange={(e) => { setAutoSlug(false); setForm((s) => ({ ...s, slug: slugify(e.target.value) })); }}
              data-testid="page-slug-input"
              placeholder="my-page"
              className="flex-1 border border-gray-300 px-3 py-2 font-mono text-sm focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          <RichTextEditor value={form.content_html} onChange={update("content_html")} />
        </div>

        <aside className="space-y-6">
          <Card title="Publish">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.published}
                onChange={update("published")}
                data-testid="page-published-toggle"
                className="w-4 h-4 accent-[#D4AF37]"
              />
              <span className="text-sm text-[#0A192F]">Published</span>
            </label>
            <p className="mt-2 text-xs text-[#0A192F]/60">
              Uncheck to save as a draft. Drafts are only visible via the admin.
            </p>
          </Card>
          <Card title="SEO Description">
            <textarea
              value={form.seo_description || ""}
              onChange={update("seo_description")}
              data-testid="page-seo-input"
              rows={4}
              placeholder="Meta description for search engines and social shares."
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none resize-y"
            />
            <p className="mt-2 text-xs text-[#0A192F]/60">
              Aim for 150-160 characters.
            </p>
          </Card>
        </aside>
      </div>
    </div>
  );
};

const Card = ({ title, children }) => (
  <div className="bg-white border border-gray-200 p-5">
    <h3 className="font-display text-xs uppercase tracking-[0.3em] text-[#0A192F]/60 font-semibold mb-4">{title}</h3>
    {children}
  </div>
);

export default PageEditor;
